import React, {useCallback, useEffect, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {useCategories, useProductSearch} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {Flex, useMultiStyleConfig} from '@chakra-ui/react'
import ManualsPageHeader from './partials/manuals-page-header'
import GridTemplate from '../../components/grid-template'
import GridCard from '../../components/grid-card'
import {CAT_MENU_DEFAULT_ROOT_CATEGORY} from '@salesforce/retail-react-app/app/constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import {useParams} from 'react-router-dom/cjs/react-router-dom'
import useCustomGetCategory from '../../custom-hooks/use-custom-categories'
import routingObject from '../../utils/static-routes'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

const Manuals = () => {
    const styles = useMultiStyleConfig('Manuals')

    const [shouldShowResult, setShouldShowResult] = useState(false)
    const [productQuery, setProductQuery] = useState('')
    const navigate = useNavigation()
    const location = useLocation()

    const {buildLocalizedUrl} = useMulticountryUtils()

    const {MANUALS_PATH} = routingObject

    const {site, locale} = useMultiSite()
    const {currency} = useCurrency()

    const params = useParams()
    const currentCategory = params.rest ? params.rest : CAT_MENU_DEFAULT_ROOT_CATEGORY

    const isNotRootCategory = currentCategory !== CAT_MENU_DEFAULT_ROOT_CATEGORY

    const categoryData = useGetCategoriesData()

    const {data: categoryDetails, isFetching: isCategoryDetailsFetching} = useCustomGetCategory(
        {
            parameters: {
                urlSegment: currentCategory,
            },
        },
        {
            enabled: isNotRootCategory,
        },
    )

    const categoryId = categoryDetails?.id ?? CAT_MENU_DEFAULT_ROOT_CATEGORY

    const {data: categoryResult, isFetching: isCategoriesListFetching} = useCategories(
        {
            parameters: {
                levels: 1,
                ids: [categoryId],
                allImages: true,
                locale: locale.id,
            },
        },
        {keepPreviousData: true},
    )

    const categories =
        categoryResult &&
        categoryResult.data &&
        categoryResult.data[0] &&
        categoryResult.data[0].categories

    const refine =
        (!categories || categories?.length === 0) && shouldShowResult && !productQuery
            ? ['cgid=' + categoryDetails?.id]
            : []

    const {data: productSearchResult, isFetching: isProductSearchFetching} = useProductSearch(
        {
            parameters: {
                q: productQuery.length > 0 ? productQuery : '',
                refine,
                expand: [
                    'promotions',
                    'variations',
                    'prices',
                    'images',
                    'custom_properties',
                    'represented_products',
                ],
                perPricebook: true,
                allVariationProperties: true,
                locale: locale.id,
                site: site.id,
                currency,
            },
        },
        {keepPreviousData: true},
    )

    const baseManualsUrl = buildLocalizedUrl(MANUALS_PATH)

    const onCategoryClick = useCallback(
        (category) => {
            const category_url = categoryData?.[category.id]?.url || ''

            if (category.onlineSubCategoriesCount === 0) setShouldShowResult(true)
            if (currentCategory === 'manuals') navigate(`${baseManualsUrl}${category_url}`)
            else navigate(`${baseManualsUrl}${category_url}`)
        },
        [location.pathname],
    )

    const handleInnerHeader = () => {
        if (productQuery.length > 0 && productSearchResult?.hits?.length > 0 && shouldShowResult) {
            return (
                <FormattedMessage
                    defaultMessage={`{number} results found for "{query}"`}
                    id="manuals-page.search-results"
                    values={{
                        number: productSearchResult?.hits?.length,
                        query: productQuery,
                    }}
                />
            )
        } else {
            return (
                <FormattedMessage
                    defaultMessage="Browse by category"
                    id="manuals-page.category-subheader"
                />
            )
        }
    }

    const onProductClick = (product) => {
        if (!product?.productId) return
        else navigate(`/s/${product?.productId}`)
    }

    useEffect(() => {
        const categories =
            categoryResult &&
            categoryResult.data &&
            categoryResult.data[0] &&
            categoryResult.data[0].categories

        if (categories?.length === 0 || !categories) setShouldShowResult(true)
        else setShouldShowResult(false)
    }, [categoryResult])

    useEffect(() => {
        if (productQuery || productQuery.length > 0) setShouldShowResult(true)
    }, [productQuery])

    const isLoading = shouldShowResult
        ? isProductSearchFetching
        : isNotRootCategory
        ? isCategoryDetailsFetching || isCategoriesListFetching
        : isCategoriesListFetching

    return (
        <Flex {...styles.manualsPageContainer} data-testid="manuals-page-container">
            <ManualsPageHeader
                noResults={!!(productQuery && shouldShowResult && !productSearchResult?.hits)}
                categoryId={categoryId}
                setProductQuery={setProductQuery}
                setShowResult={setShouldShowResult}
            />
            <GridTemplate header={handleInnerHeader()} variant="manuals">
                {isLoading && <LoadingSpinner />}
                {!shouldShowResult ||
                (productQuery && shouldShowResult && !productSearchResult?.hits) ? (
                    <>
                        {categories?.map((category) => {
                            const shouldShowCategory =
                                !category?.id.includes('commerceAdditional') &&
                                !category?.id.includes('hidden')
                            return (
                                shouldShowCategory && (
                                    <GridCard
                                        key={category.id}
                                        title={category.name}
                                        onClick={() => onCategoryClick(category)}
                                        image={category?.c_imageLink}
                                        variant="manuals"
                                    />
                                )
                            )
                        })}
                    </>
                ) : (
                    productSearchResult?.hits &&
                    productSearchResult?.hits?.map((product, i) => (
                        <GridCard
                            key={i}
                            title={product.productName}
                            onClick={() => onProductClick(product)}
                            image={product?.image?.link || product?.image?.disBaseLink}
                            variant="manuals"
                        />
                    ))
                )}
            </GridTemplate>
        </Flex>
    )
}
Manuals.getTemplateName = () => 'manuals-page'
export default Manuals
